import { useEffect, useLayoutEffect, useRef } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useSelectionNotify(ctx: ShapeManagerStateApi) {
  const { editScopeGroupId, onSelectionChange, selectedShapeIds, setAltHoveredShapeId, shapes } =
    ctx

  // Notify parent when selection changes
  const onSelectionChangeRef = useRef(onSelectionChange)

  // Assigned during render (not an effect): the notify below runs in a useLayoutEffect, which
  // fires before passive effects — an effect-based sync here would still hold the previous
  // render's callback when the notify layout effect reads it in the same commit.
  onSelectionChangeRef.current = onSelectionChange

  // Same reasoning for the shapes the notify resolves against: `shapesRef` is synced in a passive
  // effect, so it is a render behind during the layout phase. Assigning here keeps it current
  // without putting `shapes` in the notify's dep array (which would re-fire the notify on every
  // geometry tick, not just on selection changes).
  const shapesForSelectionNotifyRef = useRef(shapes)

  shapesForSelectionNotifyRef.current = shapes

  // useLayoutEffect (not useEffect): parent state driven by this (e.g. Object panel fields)
  // must update before paint, or the panel flashes the previous selection's values for a frame.
  useLayoutEffect(() => {
    const allShapes = shapesForSelectionNotifyRef.current
    const selected = selectedShapeIds
      .map((id) => allShapes.find((s) => s.id === id))
      .filter((s): s is ShapeData => s != null)

    onSelectionChangeRef.current?.(selectedShapeIds, { shapes: selected })
  }, [selectedShapeIds])

  // Clear alt-hovered shape when selection is no longer multi-select. Inside an isolated group an
  // Alt+Click highlights the one member it selects (WMPCM-13465), so that highlight stays while
  // the member is the selection.
  useEffect(() => {
    if (selectedShapeIds.length < 2) {
      setAltHoveredShapeId((prev) =>
        editScopeGroupId != null && prev != null && selectedShapeIds.includes(prev) ? prev : null
      )
    }
  }, [editScopeGroupId, selectedShapeIds, setAltHoveredShapeId])

  return { onSelectionChangeRef, shapesForSelectionNotifyRef }
}

export type SelectionNotifyApi = ReturnType<typeof useSelectionNotify>
